import { useEffect, useRef, useState } from "react";
import { loadCheckoutWebComponents } from "@checkout.com/checkout-web-components";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

// Checkout.com Sandbox Flow card-payment UI. Order + amount + 15% commission are
// server-authoritative; the order is marked paid ONLY by the Checkout.com webhook.
export const CheckoutFlow = ({ ensureOrder, onSuccess, locale, addressReady }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const initedRef = useRef(false);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!addressReady || initedRef.current) return;
    initedRef.current = true;
    let cancelled = false;
    let flowComponent = null;

    (async () => {
      setStatus("loading");
      try {
        const oid = await ensureOrder();
        const returnBase = window.location.origin.startsWith("https://")
          ? window.location.origin
          : (process.env.REACT_APP_BACKEND_URL || "");
        const { data } = await api.post(`/orders/${oid}/checkout/session`, { return_base: returnBase });
        if (cancelled) return;
        const checkout = await loadCheckoutWebComponents({
          publicKey: data.public_key,
          paymentSession: data.payment_session,
          environment: "sandbox",
          locale: locale || "en",
          onPaymentCompleted: () => onSuccess(oid),
          onError: () => {
            if (cancelled) return;
            setStatus("error");
            setErrorMsg(t("customer.cardLoadError"));
          },
        });
        if (cancelled) return;
        flowComponent = checkout.create("flow");
        flowComponent.mount(containerRef.current);
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        initedRef.current = false;
        setStatus("error");
        setErrorMsg(formatApiError(e.response?.data?.detail) || t("customer.cardLoadError"));
      }
    })();

    return () => {
      cancelled = true;
      try { flowComponent && flowComponent.unmount(); } catch (_) { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressReady]);

  if (!addressReady) return <div className="text-xs text-amber-600" data-testid="checkout-flow-need-address">{t("customer.enterAddress")}</div>;

  return (
    <div data-testid="checkout-flow" className="pt-1">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> {t("customer.cardPay")}
      </div>
      {status === "loading" && <div data-testid="checkout-flow-loading" className="text-sm text-muted-foreground py-4 text-center">…</div>}
      {status === "error" && <div data-testid="checkout-flow-error" className="text-sm text-red-600 py-2">{errorMsg}</div>}
      <div ref={containerRef} data-testid="checkout-flow-container" />
    </div>
  );
};
